// src/pages/SuccessStories.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Star, 
  TrendingUp, 
  Users, 
  Calendar, 
  Quote,
  Clock,
  MapPin
} from 'lucide-react';
import '../styles/successStories.css'; // Create this CSS file

const SuccessStories = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [stories, setStories] = useState([]);
  
  // Categories for success stories
  const categories = [
    { id: 'all', name: 'All Stories' },
    { id: 'new-restaurants', name: 'New Restaurants' },
    { id: 'established', name: 'Established Businesses' },
    { id: 'growth', name: 'Growth Stories' },
    { id: 'international', name: 'International Success' }
  ];
  
  // Featured success metrics
  const metrics = [
    { 
      id: 1, 
      value: '87%', 
      label: 'Average Increase in Bookings',
      icon: <Calendar size={24} />,
      color: '#4CAF50'
    },
    { 
      id: 2, 
      value: '35%', 
      label: 'Higher Customer Retention',
      icon: <Users size={24} />,
      color: '#2196F3'
    },
    { 
      id: 3, 
      value: '42%', 
      label: 'Growth in Annual Revenue',
      icon: <TrendingUp size={24} />,
      color: '#F44336'
    },
    { 
      id: 4, 
      value: '4.8', 
      label: 'Average Partner Rating',
      icon: <Star size={24} />,
      color: '#FF9800'
    }
  ];
  
  useEffect(() => {
    // Simulate API fetch
    setIsLoading(true);
    setTimeout(() => {
      setStories(getSuccessStories());
      setIsLoading(false);
    }, 800);
  }, []);
  
  // Filter stories based on active category
  const filteredStories = activeCategory === 'all'
    ? stories
    : stories.filter(story => story.category === activeCategory);
  
  // Sample data - in a real app, this would come from an API
  const getSuccessStories = () => {
    return [
      {
        id: 1,
        title: "From Struggling Local Spot to Neighborhood Favorite",
        restaurant: "Al-Madina Grill",
        category: "growth",
        location: "New York, NY",
        owner: "Mohammed Ahmed",
        featured: true,
        image: "https://images.unsplash.com/photo-1590846406792-0adc7f938f1d?q=80&w=2069",
        ownerImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974",
        quote: "Before joining the platform, we were struggling to fill tables. Now we're booked solid most nights and have expanded our dining area twice!",
        story: "When Mohammed Ahmed opened Al-Madina Grill in 2019, he faced the typical challenges of a new restaurant: low visibility, inconsistent customer flow, and difficulty standing out in a competitive market. After joining our platform in early 2020, everything changed. With increased visibility and the halal-friendly tag, Al-Madina started attracting both local and tourist diners looking specifically for authentic halal options.",
        results: [
          "Increased bookings by 157% in the first 6 months",
          "Expanded from 12 to 32 tables to accommodate demand",
          "Hired 8 additional staff members",
          "Added catering services due to increased demand"
        ],
        timeframe: "18 months",
        rating: 4.9,
        videoTestimonial: true
      },
      {
        id: 2,
        title: "Building a Loyal Customer Base in a New City",
        restaurant: "Saffron House",
        category: "new-restaurants",
        location: "Chicago, IL",
        owner: "Fatima Khan",
        featured: true,
        image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070",
        ownerImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1974",
        quote: "As a newcomer to Chicago's restaurant scene, I was worried about establishing a presence. This platform helped us connect with exactly the right customers from day one.",
        story: "Fatima Khan moved to Chicago with a dream of bringing authentic Pakistani cuisine to the city. With no local connections and limited marketing budget, she faced significant obstacles. By listing Saffron House on our platform with detailed menu information and halal certification prominently displayed, she was able to immediately tap into the local Muslim community and halal food enthusiasts.",
        results: [
          "Reached profitability within 4 months of opening",
          "Built a database of over 1,500 repeat customers",
          "Achieved 4.8-star average rating from 300+ reviews",
          "Featured in Chicago Tribune's 'Best New Restaurants'"
        ],
        timeframe: "12 months",
        rating: 4.8,
        videoTestimonial: false
      },
      {
        id: 3,
        title: "From Food Truck to Full-Service Restaurant",
        restaurant: "Falafel King",
        category: "growth",
        location: "Austin, TX",
        owner: "Ibrahim Khalid",
        featured: false,
        image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074",
        ownerImage: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=1974",
        quote: "The platform's reach helped us build such a loyal following that we were able to open our brick-and-mortar location with pre-existing customers waiting!",
        story: "Ibrahim started with a single food truck serving falafel and shawarma to Austin's lunch crowds. After listing on our platform, he saw a surge in customers, many specifically searching for halal options in the area. The increased visibility and positive reviews gave him the confidence and financial backing to open a full-service restaurant.",
        results: [
          "Transitioned from food truck to 80-seat restaurant",
          "Increased revenue by 230% year-over-year",
          "Created 15 new jobs in the local community",
          "Expanded menu from 8 to 24 items"
        ],
        timeframe: "24 months",
        rating: 4.7,
        videoTestimonial: true
      },
      {
        id: 4,
        title: "Revitalizing a 25-Year Family Business",
        restaurant: "Istanbul Kitchen",
        category: "established",
        location: "Boston, MA",
        owner: "Mehmet Yilmaz",
        featured: false,
        image: "https://images.unsplash.com/photo-1552566626-52f8b828add9?q=80&w=2070",
        ownerImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070",
        quote: "Our restaurant had been serving the community for 25 years, but we needed to adapt to changing times. This platform helped us reach a whole new generation of customers.",
        story: "Istanbul Kitchen had been a Boston staple for decades, but was struggling to attract younger diners. The second-generation owner, Mehmet, decided to partner with our platform to modernize their approach. With online reservations, detailed halal information, and increased visibility among millennials searching for authentic Turkish cuisine, Istanbul Kitchen found new life.",
        results: [
          "Reduced average customer age from 55+ to include 25-40 demographic",
          "Increased weekday dinner bookings by 45%",
          "Successfully introduced weekend brunch service",
          "Generated 35% of business through online reservations"
        ],
        timeframe: "16 months",
        rating: 4.6,
        videoTestimonial: false
      },
      {
        id: 5,
        title: "International Expansion Success Story",
        restaurant: "Levant Fusion",
        category: "international",
        location: "Toronto, Canada",
        owner: "Layla Bakhash",
        featured: false,
        image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070",
        ownerImage: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1961",
        quote: "Expanding to a new country was daunting, but having a platform that understood halal dining needs made all the difference in quickly establishing ourselves in Toronto.",
        story: "After success in London, Layla decided to bring her Lebanese-fusion concept to Canada. By joining our platform before doors even opened, she was able to generate buzz and pre-bookings. The platform's specific filters for halal-certified and Middle Eastern cuisine helped targeted diners discover Levant Fusion immediately.",
        results: [
          "Fully booked for first three months of operation",
          "Built email marketing list of 5,000+ subscribers pre-opening",
          "Received features in 3 major Toronto publications",
          "Achieved profitability 70% faster than London location"
        ],
        timeframe: "10 months",
        rating: 4.9,
        videoTestimonial: true
      },
      {
        id: 6,
        title: "Small Town Success on a Global Platform",
        restaurant: "Olive & Mint",
        category: "new-restaurants",
        location: "Ann Arbor, MI",
        owner: "Zainab Mansour",
        featured: false,
        image: "https://images.unsplash.com/photo-1600891964599-f61f4298bdbc?q=80&w=2070",
        ownerImage: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1976",
        quote: "In a college town, we needed to reach students and faculty searching for halal options. This platform connected us with exactly the right audience.",
        story: "Zainab opened Olive & Mint near the University of Michigan campus, offering Mediterranean cuisine with a modern twist. By highlighting her halal certifications and student discounts on our platform, she quickly became the go-to spot for the university's Muslim student association and faculty members looking for halal dining options.",
        results: [
          "Became official caterer for 5 Muslim student organizations",
          "Created popular exam-week special promotion through the platform",
          "Doubled weekend lunch service capacity",
          "Expanded delivery radius to cover entire campus"
        ],
        timeframe: "14 months",
        rating: 4.5,
        videoTestimonial: false
      }
    ];
  };
  
  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading success stories...</p>
      </div>
    );
  }
  
  return (
    <div className="success-stories-page">
      {/* Hero Section */}
      <div className="success-stories-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1 className="display-4 fw-bold text-white mb-4">Real Success Stories from Real Restaurant Partners</h1>
              <p className="lead text-white opacity-90 mb-4">
                Discover how restaurant owners have transformed their businesses through our platform
              </p>
              <Link to="/register-restaurant" className="btn btn-light btn-lg px-4">
                Join Our Community
              </Link>
            </div>
            <div className="col-lg-6 d-none d-lg-block">
              <div className="testimonial-cards">
                <div className="testimonial-card card-1">
                  <div className="testimonial-content">
                    <Quote size={20} className="quote-icon" />
                    <p>"Bookings increased by 80% in just three months!"</p>
                    <div className="testimonial-author">
                      <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974" alt="Restaurant Owner" className="author-image" />
                      <div className="author-info">
                        <div className="author-name">Mohammed A.</div>
                        <div className="author-restaurant">Al-Madina Grill</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="testimonial-card card-2">
                  <div className="testimonial-content">
                    <Quote size={20} className="quote-icon" />
                    <p>"Our restaurant was fully booked every weekend after joining!"</p>
                    <div className="testimonial-author">
                      <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1974" alt="Restaurant Owner" className="author-image" />
                      <div className="author-info">
                        <div className="author-name">Fatima K.</div>
                        <div className="author-restaurant">Saffron House</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="testimonial-card card-3">
                  <div className="testimonial-content">
                    <Quote size={20} className="quote-icon" />
                    <p>"We expanded to a second location due to increased demand!"</p>
                    <div className="testimonial-author">
                      <img src="https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=1974" alt="Restaurant Owner" className="author-image" />
                      <div className="author-info">
                        <div className="author-name">Ibrahim K.</div>
                        <div className="author-restaurant">Falafel King</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Success Metrics */}
      <div className="success-metrics py-5">
        <div className="container">
          <div className="row g-4">
            {metrics.map(metric => (
              <div key={metric.id} className="col-md-6 col-lg-3">
                <div className="metric-card">
                  <div className="metric-icon" style={{ backgroundColor: `${metric.color}25` }}>
                    <div className="icon-inner" style={{ color: metric.color }}>
                      {metric.icon}
                    </div>
                  </div>
                  <div className="metric-value" style={{ color: metric.color }}>{metric.value}</div>
                  <div className="metric-label">{metric.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Category Navigation */}
      <div className="container mt-5 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0 fw-bold">Browse Success Stories</h2>
        </div>
        
        <div className="category-tabs">
          {categories.map(category => (
            <button
              key={category.id}
              className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
      
      {/* Featured Stories */}
      {filteredStories.filter(story => story.featured).length > 0 && (
        <div className="container mb-5">
          <div className="featured-stories">
            {filteredStories.filter(story => story.featured).map(story => (
              <div key={story.id} className="featured-story-card">
                <div className="row g-0">
                  <div className="col-lg-6">
                    <div className="featured-story-image">
                      <img src={story.image} alt={story.restaurant} />
                      {story.videoTestimonial && (
                        <div className="video-badge">
                          <span>Watch Video</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="featured-story-content">
                      <div className="featured-badge">Featured Story</div>
                      <h3>{story.title}</h3>
                      <div className="restaurant-info">
                        <div className="restaurant-name">{story.restaurant}</div>
                        <div className="restaurant-location">
                          <MapPin size={14} className="me-1" />
                          {story.location}
                        </div>
                      </div>
                      <div className="quote-box">
                        <Quote size={24} className="quote-icon" />
                        <blockquote>{story.quote}</blockquote>
                        <div className="quote-author">
                          <img src={story.ownerImage} alt={story.owner} className="author-image" />
                          <div className="author-name">{story.owner}</div>
                        </div>
                      </div>
                      <div className="story-highlights">
                        <div className="highlight-item">
                          <Clock size={16} className="me-2" />
                          <span>Timeframe: {story.timeframe}</span>
                        </div>
                        <div className="highlight-item">
                          <Star size={16} className="me-2" />
                          <span>Rating: {story.rating}/5.0</span>
                        </div>
                      </div>
                      <Link to={`/success-stories/${story.id}`} className="btn btn-primary mt-3">
                        Read Full Story
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Regular Stories */}
      <div className="container">
        <div className="row g-4">
          {filteredStories.filter(story => !story.featured).map(story => (
            <div key={story.id} className="col-md-6 col-lg-4">
              <div className="story-card">
                <div className="story-image">
                  <img src={story.image} alt={story.restaurant} />
                  {story.videoTestimonial && (
                    <div className="video-indicator">
                      <span>Video</span>
                    </div>
                  )}
                </div>
                <div className="story-content">
                  <h4>{story.title}</h4>
                  <div className="story-meta">
                    <div className="restaurant-name">{story.restaurant}</div>
                    <div className="restaurant-location">
                      <MapPin size={12} className="me-1" />
                      {story.location}
                    </div>
                  </div>
                  <div className="story-quote">
                    <Quote size={16} className="quote-icon me-1" />
                    <p>{story.quote.substring(0, 100)}...</p>
                  </div>
                  <div className="story-footer">
                    <div className="owner-info">
                      <img src={story.ownerImage} alt={story.owner} className="owner-image" />
                      <span>{story.owner}</span>
                    </div>
                    <Link to={`/success-stories/${story.id}`} className="story-link">
                      Read More <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="success-stories-cta mt-5 py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <h2 className="text-white fw-bold mb-3">Ready to Write Your Success Story?</h2>
              <p className="text-white opacity-90 mb-0">Join thousands of restaurant partners who've transformed their business with our platform</p>
            </div>
            <div className="col-lg-5 text-lg-end mt-4 mt-lg-0">
              {/* <Link to="/register-restaurant" className="btn btn-light btn-lg px-4 me-3">
                Register Your Restaurant
              </Link> */}
              <Link to="/partner-support" className="btn btn-outline-light btn-lg px-4">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessStories;